import { useEffect, useRef } from 'react'

import { getPageAttributes } from 'Pods/SearchPage/Shared/Tracking/pageAttributes'
import safely from 'Pods/SearchPage/Shared/Tracking/safely'
import { searchPageStore } from 'Pods/SearchPage/Store/index'

import { rememberDownloadOrigin } from 'Utils/downloadTracking'
import ImpressionsBuffer from 'Utils/ImpressionsBuffer'
import { prepareTrackingItem } from 'Utils/PrepareTrackingItem'
import ResolveProductPricing from 'Utils/ResolveProductPricing'

// CTR impressions feed the item performance scores; batched like ItemsListing's TrackingProvider.
export const CTR_POST_INTERVAL = 10000

const TRACKING_GLOBALS = [
  'handleImpression',
  'trackSelectItem',
  'trackSelectContent',
  'trackAddToCart',
  'trackRemoveFromCart',
  'trackAddToWishList',
]

const getCart = () => window.pageConfig?.cart
const getUserId = () => window.pageConfig?.currentUserId || ''

// Mirrors the legacy listing page (listing_page/tracking.js prepareItemTracking): a discount is
// only reported for an active sale-off or CLD price, otherwise the base price goes out as is.
const resolvePricing = (attributes, id) => {
  const pricing = ResolveProductPricing({ cart: getCart(), attributes, id })

  if (['saleOff', 'CLD'].includes(pricing.ribbon) && pricing.discount > 0) {
    return {
      price: pricing.price.toFixed(2),
      discount: (pricing.originalPrice - pricing.price).toFixed(2),
    }
  }

  return { price: Number(attributes.price || 0).toFixed(2), discount: 0 }
}

const buildItem = ({ id, attributes, index, gaAttributes = {}, itemListId }) => {
  const pricing = resolvePricing(attributes, id)

  return prepareTrackingItem({
    id,
    title: attributes.title,
    affiliation: gaAttributes.affiliation,
    index,
    itemBrand: gaAttributes.itemBrand,
    itemCategory: gaAttributes.itemCategory,
    subcategory: gaAttributes.itemCategory2,
    itemCategory3: gaAttributes.itemCategory3,
    itemCategory4: gaAttributes.itemCategory4,
    itemCategory5: gaAttributes.itemCategory5,
    itemListId,
    itemListName: itemListId,
    itemVariant: gaAttributes.itemVariant,
    locationId: gaAttributes.locationId,
  }, pricing)
}

const pushEcommerceEvent = (payload) => {
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ ...payload, ...getPageAttributes() })
}

// The list name comes from the items, not the page: a pending row flushed after a filter change
// still belongs to the list it was seen in.
const pushViewItemList = (items) => {
  if (items.length === 0) {
    return
  }

  const listId = items[0].item_list_id
  pushEcommerceEvent({
    event: 'view_item_list',
    user_id: getUserId(),
    ecommerce: { item_list_id: listId, item_list_name: listId, items },
    _clear: true,
  })
}

// Same 70% rule as ModelCardImage's useInView and the legacy ListingPage viewport scan.
const VISIBLE_RATIO = 0.7

const getVisibleCardIds = () => (
  Array.from(document.querySelectorAll('.search-page__results-grid .js-fast-listing-item[data-attributes]'))
    .filter((card) => {
      const rect = card.getBoundingClientRect()
      const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0)
      return rect.height > 0 && rect.right > 0 && rect.left < window.innerWidth
        && visibleHeight / rect.height >= VISIBLE_RATIO
    })
    .map((card) => {
      try {
        return Number(JSON.parse(card.getAttribute('data-attributes')).id)
      } catch (error) {
        return null
      }
    })
    .filter(Boolean)
)

/**
 * GA4 ecommerce events for the result grid: view_item_list (batched per row as cards enter the
 * viewport), select_item, select_content, add_to_cart, remove_from_cart and add_to_wishlist, plus
 * the CTR impressions POST. ModelCard reports through the window globals the legacy listing page
 * defines in listing_page/tracking.js, which this page does not load.
 *
 * @returns {{ isTrackingInfoAvailable: boolean, areTrackingUtilsAvailable: boolean }} the flags
 *   ModelCardImage waits for before it reports an impression.
 */
export default function useResultsTracking({
  results,
  resultIdsSignature,
  trackingInfoIds,
  gaAttributesById,
  itemListId,
  pageOffset,
  perPage,
  modelsPerRow,
  areTrackingUtilsAvailable,
}) {
  const isTrackingInfoAvailable = Boolean(resultIdsSignature) && trackingInfoIds === resultIdsSignature

  // Read by the window globals at call time. Assigned during render, not in an effect: when the
  // flags flip, ModelCardImage's effect reports the visible cards before a parent effect would run.
  const latest = useRef({})
  latest.current = {
    isReady: isTrackingInfoAvailable && areTrackingUtilsAvailable,
    results,
    gaAttributesById,
    itemListId,
    pageOffset,
    perPage,
    modelsPerRow: Math.max(Number(modelsPerRow) || 1, 1),
  }

  const bufferRef = useRef(null)
  if (!bufferRef.current) {
    bufferRef.current = new ImpressionsBuffer()
  }
  const trackedCountRef = useRef(0)
  const ctrIdsRef = useRef([])

  const flushCtrImpressions = () => {
    const itemIds = ctrIdsRef.current.splice(0)
    if (itemIds.length > 0 && typeof window.postCtrImpressions === 'function') {
      window.postCtrImpressions(itemIds, { perPage: latest.current.perPage })
    }
  }

  const flushPendingImpressions = safely(() => {
    pushViewItemList(bufferRef.current.flushImpressions(bufferRef.current.getCount()))
  })

  // A new result set or a new list name (a tag can rename the list and keep the same models) starts
  // a fresh impression count. The row still pending goes out first, under the list it was seen in.
  const resultSetKey = `${resultIdsSignature}|${itemListId}`
  const previousResultSetKeyRef = useRef(resultSetKey)

  useEffect(() => {
    if (previousResultSetKeyRef.current === resultSetKey) {
      return
    }
    previousResultSetKeyRef.current = resultSetKey

    flushPendingImpressions()
    bufferRef.current.clear()
    trackedCountRef.current = 0

    // New ids reset the tracking info, and the cards report again once it lands. A rename keeps
    // the cards ready, so nothing would re-fire them: report the ones already in view here.
    if (latest.current.isReady && typeof window.handleImpression === 'function') {
      getVisibleCardIds().forEach((itemId) => window.handleImpression(itemId))
    }
  }, [resultSetKey])

  useEffect(() => {
    const findModel = (itemId) => {
      const { results: currentResults, pageOffset: offset } = latest.current
      const position = currentResults.findIndex((model) => Number(model.id) === Number(itemId))
      return position === -1 ? null : { model: currentResults[position], index: offset + position }
    }

    const enrichClickAttributes = (itemAttributes) => {
      const { gaAttributesById: gaById, itemListId: listId } = latest.current
      const ga = gaById[String(itemAttributes.id)] || {}

      return buildItem({
        id: itemAttributes.id,
        attributes: itemAttributes,
        index: itemAttributes.index,
        gaAttributes: {
          ...ga,
          itemBrand: itemAttributes.itemBrand || ga.itemBrand,
          itemCategory: itemAttributes.itemCategory || ga.itemCategory,
          itemCategory2: itemAttributes.subcategory || ga.itemCategory2,
          itemVariant: itemAttributes.productType || ga.itemVariant,
        },
        itemListId: itemAttributes.searchQuery || listId,
      })
    }

    window.handleImpression = safely((itemId) => {
      const current = latest.current
      if (!current.isReady) {
        return
      }

      const match = findModel(itemId)
      if (!match) {
        return
      }

      const item = buildItem({
        id: match.model.id,
        attributes: match.model.attributes || {},
        index: match.index,
        gaAttributes: current.gaAttributesById[String(match.model.id)],
        itemListId: current.itemListId,
      })
      if (!bufferRef.current.addImpressionToQueue(item)) {
        return
      }

      ctrIdsRef.current.push(Number(match.model.id))

      const buffered = bufferRef.current.getCount()
      const isRowComplete = buffered >= current.modelsPerRow
      const isLastRow = trackedCountRef.current + buffered >= current.results.length
      if (!isRowComplete && !isLastRow) {
        return
      }

      const items = bufferRef.current.flushImpressions(current.modelsPerRow)
      trackedCountRef.current += items.length
      pushViewItemList(items)
    })

    window.trackSelectItem = safely((itemAttributes) => {
      if (!getCart()) return
      const item = enrichClickAttributes(itemAttributes)

      try {
        // Read by the item page to report the clicked position (legacy tracking.js contract).
        localStorage.setItem(`indexClick${itemAttributes.id}`, itemAttributes.originalIndex)
      } catch (error) {
        // Storage can be unavailable (private mode); the event still goes out.
      }

      rememberDownloadOrigin({
        itemId: itemAttributes.id,
        pageType: 'listing_page',
        itemListName: item.item_list_name || '',
      })

      pushEcommerceEvent({
        event: 'select_item',
        user_id: getUserId(),
        ecommerce: {
          currency: 'USD',
          value: item.price,
          item_list_name: item.item_list_name,
          item_list_id: item.item_list_id,
          items: [item],
        },
        _clear: true,
      })
    })

    window.trackSelectContent = safely((itemAttributes) => {
      if (!getCart()) return
      const item = enrichClickAttributes(itemAttributes)

      pushEcommerceEvent({
        event: 'select_content',
        ecommerce: {
          item_list_name: item.item_list_name,
          item_list_id: item.item_list_id,
          items: [item],
        },
      })
    })

    window.trackAddToCart = safely((itemAttributes, eventSource) => {
      if (!getCart()) return
      const item = enrichClickAttributes(itemAttributes)

      pushEcommerceEvent({
        event: 'add_to_cart',
        user_id: getUserId(),
        event_source: eventSource,
        ecommerce: { currency: 'USD', value: item.price, items: [item] },
        _clear: true,
      })
    })

    window.trackRemoveFromCart = safely((itemAttributes) => {
      if (!getCart()) return
      const item = enrichClickAttributes(itemAttributes)

      pushEcommerceEvent({
        event: 'remove_from_cart',
        user_id: getUserId(),
        ecommerce: { currency: 'USD', value: item.price, items: [item] },
        _clear: true,
      })
    })

    window.trackAddToWishList = safely((itemAttributes, eventSource) => {
      if (!getCart()) return
      const item = enrichClickAttributes(itemAttributes)

      pushEcommerceEvent({
        event: 'add_to_wishlist',
        user_id: getUserId(),
        event_source: eventSource,
        ecommerce: { currency: 'USD', value: item.price, items: [item] },
      })
    })

    // The impressions POST carries window.location.href, and a fetch pushes the new canonical URL
    // right after it writes the results - so the pending ids go out synchronously on that write,
    // while the address bar still names the result set they were seen in.
    const unsubscribe = searchPageStore.subscribe((state, previousState) => {
      if (state.results !== previousState.results) {
        flushCtrImpressions()
      }
    })
    const handlePageHide = () => {
      flushPendingImpressions()
      flushCtrImpressions()
    }
    const intervalId = setInterval(flushCtrImpressions, CTR_POST_INTERVAL)
    window.addEventListener('pagehide', handlePageHide)

    return () => {
      unsubscribe()
      clearInterval(intervalId)
      window.removeEventListener('pagehide', handlePageHide)
      handlePageHide()
      TRACKING_GLOBALS.forEach((name) => delete window[name])
    }
  }, [])

  return { isTrackingInfoAvailable, areTrackingUtilsAvailable }
}
